import React, { useCallback, useEffect, useRef, useState } from "react";

import Quill from "quill";
import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "src/base-components/Button";
import {
  TextEditor,
  TextEditorRef,
} from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { useCreateCaseComment } from "src/cases/queries/history";
import { toastActions } from "src/design-system/Toast/utils";
import { CasePageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseCommentEditor: React.FC = () => {
  const { case_id: caseId, wsId } = useParamsDecode<CasePageParams>();
  const { mutateAsync: createCaseComment, isPending } = useCreateCaseComment({
    caseId,
  });

  const [comment, setComment, removeCommentFromStorage] = useLocalStorage(
    `caseComment-${wsId}-${caseId}`,
    "",
  );

  const handleSubmit = async () => {
    const trimmedComment = comment.trim();

    if (trimmedComment === "") {
      toastActions.failure({
        title: "Failed to add comment",
        description: "Comment cannot be empty",
      });
      return;
    }

    try {
      await createCaseComment({ comment });
      removeCommentFromStorage();
    } catch (error) {
      logger.error(error);
      toastActions.failure({
        title: "Failed to add comment",
        description: "Please try again later",
      });
    }
  };

  const handleChange = useCallback(
    (value: string) => {
      if (value === "") {
        removeCommentFromStorage();
      } else {
        setComment(value);
      }
    },
    [removeCommentFromStorage, setComment],
  );

  return (
    <CommentEditor
      dataLoc="case-comment-editor"
      isPending={isPending}
      value={comment}
      autoFocus
      onChange={handleChange}
      onSubmit={handleSubmit}
    />
  );
};

type CommentEditorProps = {
  dataLoc?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  autoFocus?: boolean;
  isPending?: boolean;
  value: string;
};

const CommentEditor: React.FC<CommentEditorProps> = ({
  dataLoc,
  value,
  onChange,
  autoFocus = false,
  isPending = false,
  onSubmit,
}) => {
  const toolbarRef = useRef<HTMLUListElement>(null);
  const editorRef = useRef<TextEditorRef>(null);
  const [quill, setQuill] = useState<Quill | null>(null);

  const isEditorEmpty = useIsQuillContentEmpty(quill, value);

  return (
    <div
      className={twJoin(
        "flex flex-col rounded-lg border border-gray-200 bg-white p-3",
        "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-500/25",
        "group",
      )}
    >
      <TextEditor
        ref={editorRef}
        autoFocus={autoFocus}
        dataLoc={dataLoc}
        placeholder="Add your comment here..."
        toolbarRef={toolbarRef}
        value={value}
        keepEmptyLines
        onChange={onChange}
        onReady={setQuill}
      />
      <div className="flex justify-between gap-x-3 pt-3">
        <Toolbar ref={toolbarRef} exclude={["heading"]} />
        <div className="flex gap-x-3">
          <Button
            dataLoc={`${dataLoc}-save-button`}
            disabled={isEditorEmpty}
            loading={isPending}
            size="sm"
            variant="primary"
            onClick={onSubmit}
          >
            Add comment
          </Button>
        </div>
      </div>
    </div>
  );
};

const useIsQuillContentEmpty = (quill: Quill | null, initialValue: string) => {
  const [isEditorEmpty, setIsEditorEmpty] = useState(
    initialValue.trim().length === 0,
  );

  useEffect(() => {
    if (!quill) return;

    const handleTextChange = () => {
      const value = quill.getText();
      setIsEditorEmpty(value.trim().length === 0);
    };

    handleTextChange();
    quill.on("text-change", handleTextChange);

    return () => {
      quill.off("text-change", handleTextChange);
    };
  }, [quill]);

  return isEditorEmpty;
};
